import { Hex } from "viem";

export enum Contract {
  USDC = "USDC",
  HEAD2HEAD_BATTLES = "HEAD2HEAD_BATTLES",
  HYPEBOARD_BATTLES = "HYPEBOARD_BATTLES",
}

export enum ContractFunc {
  // USDC contract functions
  APPROVE_USDC = "approve",
  GET_USDC_BALANCE = "balanceOf",
  WITHDRAW_USDC = "transfer",

  // Battle contract functions (shared between HEAD2HEAD and HYPEBOARD)
  CREATE_BATTLE = "createBattle",
  CREATE_BATTLE_WITH_TICKETS = "createBattleWithTickets",
  MAKE_PREDICTION = "makePrediction",
  MAKE_PREDICTION_WITH_TICKETS = "makePredictionWithTickets",
  ADD_SIZE = "updateAmount",
  ADD_SIZE_TICKETS = "updateAmountWithTickets",
  CLAIM_MARKETMAKER_INCENTIVES = "claimMarketMakerIncentives",
  CLAIM_WIN = "claimWin",
  CLAIM_REFUND = "claimRefund",
}

export interface ApproveUsdcArgs {
  amount: number;
}

export interface WithdrawUsdcArgs {
  destinationAddress: Hex; // address to send USDC to
  amount: number;
}

export interface CreateBattleArgs {
  manifest: {
    option0Id: string; // spotify id
    option1Id: string; // spotify id
  };
  option: 1 | 2;
  amount: number;
  permitSig?: string;
  useNextWindow?: boolean;
  useRelayer?: boolean;
}

export interface CreateBattleWithTicketsArgs {
  manifest: {
    option0Id: string; // spotify id
    option1Id: string; // spotify id
  };
  option: 1 | 2;
  ticketsIds: number[];
  useNextWindow?: boolean;
  useRelayer?: boolean;
}

export interface MakePredictionArgs {
  battleId: number | undefined;
  option: number;
  amount: number;
  permitSig?: string;
  useRelayer?: boolean | string;
}

export interface MakePredictionWithTicketsArgs {
  battleId: number;
  ticketsIds: number[];
  option: number;
  useRelayer?: boolean;
}

export interface AddSizeArgs {
  battleId: number;
  option: number;
  amount: number;
  permitSig?: string;
  useRelayer?: boolean;
}

export interface AddSizeWithTicketsArgs {
  battleId: number;
  option: number;
  ticketsIds: number[];
  useRelayer?: boolean;
}

export interface ClaimMarketMakerIncentivesArgs {
  battleIds: number[];
}

export interface ClaimWinArgs {
  battleIds: number[];
  useRelayer?: boolean;
}

export interface ClaimRefundArgs {
  battleIds: number[];
  useRelayer?: boolean;
}

export type ContractTxDataMap = {
  [ContractFunc.APPROVE_USDC]: ApproveUsdcArgs;
  [ContractFunc.GET_USDC_BALANCE]: Record<string, never>;
  [ContractFunc.WITHDRAW_USDC]: WithdrawUsdcArgs;
  [ContractFunc.CREATE_BATTLE]: CreateBattleArgs;
  [ContractFunc.CREATE_BATTLE_WITH_TICKETS]: CreateBattleWithTicketsArgs;
  [ContractFunc.MAKE_PREDICTION]: MakePredictionArgs;
  [ContractFunc.MAKE_PREDICTION_WITH_TICKETS]: MakePredictionWithTicketsArgs;
  [ContractFunc.ADD_SIZE]: AddSizeArgs;
  [ContractFunc.ADD_SIZE_TICKETS]: AddSizeWithTicketsArgs;
  [ContractFunc.CLAIM_MARKETMAKER_INCENTIVES]: ClaimMarketMakerIncentivesArgs;
  [ContractFunc.CLAIM_WIN]: ClaimWinArgs;
  [ContractFunc.CLAIM_REFUND]: ClaimRefundArgs;
};

// Add contract mapping to function names
export const ContractFuncMap: Record<ContractFunc, Contract[]> = {
  [ContractFunc.APPROVE_USDC]: [Contract.USDC],
  [ContractFunc.GET_USDC_BALANCE]: [Contract.USDC],
  [ContractFunc.WITHDRAW_USDC]: [Contract.USDC],
  [ContractFunc.CREATE_BATTLE]: [
    Contract.HEAD2HEAD_BATTLES,
    Contract.HYPEBOARD_BATTLES,
  ],
  [ContractFunc.CREATE_BATTLE_WITH_TICKETS]: [
    Contract.HEAD2HEAD_BATTLES,
    Contract.HYPEBOARD_BATTLES,
  ],
  [ContractFunc.MAKE_PREDICTION]: [
    Contract.HEAD2HEAD_BATTLES,
    Contract.HYPEBOARD_BATTLES,
  ],
  [ContractFunc.MAKE_PREDICTION_WITH_TICKETS]: [
    Contract.HEAD2HEAD_BATTLES,
    Contract.HYPEBOARD_BATTLES,
  ],
  [ContractFunc.ADD_SIZE]: [
    Contract.HEAD2HEAD_BATTLES,
    Contract.HYPEBOARD_BATTLES,
  ],
  [ContractFunc.ADD_SIZE_TICKETS]: [
    Contract.HEAD2HEAD_BATTLES,
    Contract.HYPEBOARD_BATTLES,
  ],
  [ContractFunc.CLAIM_MARKETMAKER_INCENTIVES]: [Contract.HEAD2HEAD_BATTLES],
  [ContractFunc.CLAIM_WIN]: [
    Contract.HEAD2HEAD_BATTLES,
    Contract.HYPEBOARD_BATTLES,
  ],
  [ContractFunc.CLAIM_REFUND]: [Contract.HEAD2HEAD_BATTLES],
};
